import type { IncidentSummary } from "../api/incidentLog";
import { DetectionCluster, pointInGeometry } from "./detectionClusters";

// Incident markers/hulls are drawn by the Incidents layer
// (utils/incidentFeatures.ts); this file only ties hotspot clusters to
// the incidents they belong to.

/**
 * Which incident(s) a hotspot cluster on the map belongs to.
 *
 * The backend never stores an observation → incident link; it merges each
 * fresh detection into the active incident whose footprint it lies within the
 * link distance of, and the footprint is just the set of absorbed detection
 * coordinates. So the map-side question "is this cluster incident #150?" is
 * "do #150's absorbed detections fall inside this cluster's outline?" —
 * answered here on data already in the browser.
 *
 * A cluster can span two incidents (the user's cluster distance is wider
 * than the incident link distance, or two fires grew together); all matches
 * are returned, the one with the most detections inside first.
 */
export const matchClustersToIncidents = (
  clusters: DetectionCluster[],
  incidents: IncidentSummary[] | undefined
): Map<string, number[]> => {
  const result = new Map<string, number[]>();
  const candidates = (incidents ?? []).filter(
    (i) =>
      i.active &&
      (i.kinds ?? [i.kind]).includes("hotspot") &&
      i.footprint &&
      i.footprint.length
  );
  if (!clusters.length || !candidates.length) return result;

  for (const cluster of clusters) {
    // Cheap bbox reject before the point-in-polygon walk: footprints can run
    // to thousands of points and hull rings to hundreds of vertices.
    const ring = cluster.hull.coordinates[0] ?? [];
    let minLon = Infinity, minLat = Infinity, maxLon = -Infinity, maxLat = -Infinity;
    for (const [lon, lat] of ring) {
      if (lon < minLon) minLon = lon;
      if (lon > maxLon) maxLon = lon;
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
    }
    const hits: { id: number; inside: number }[] = [];
    for (const incident of candidates) {
      let inside = 0;
      for (const point of incident.footprint!) {
        const [lon, lat] = point;
        if (lon < minLon || lon > maxLon || lat < minLat || lat > maxLat) continue;
        if (pointInGeometry([lon, lat], cluster.hull)) inside += 1;
      }
      if (inside > 0) hits.push({ id: incident.id, inside });
    }
    if (hits.length) {
      hits.sort((a, b) => b.inside - a.inside || b.id - a.id);
      result.set(
        cluster.id,
        hits.map((h) => h.id)
      );
    }
  }
  return result;
};
